import { useResource, type RouteResource } from '@atlassian/react-resource-router';
import { setUserPreference } from './setUserPreference.tsx';
import type { PreferenceOptions, UserPreferences } from './UserPreferences.types.tsx';

export const useUserPreference = (
	resource: RouteResource<UserPreferences>,
	key: string,
	options?: PreferenceOptions,
): {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	value: any;
	setValue: (newValue: string) => void;
	error: // oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| Record<string, any>
		| Error
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| (Record<string, any> & Error)
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| (Error & Record<string, any>)
		| null;
	loading: boolean;
} => {
	const { data, update, error, loading } = useResource(resource);

	const value = data?.[key];

	const setValue = (newValue: string) => {
		const formattedNewValue = options?.skipValueFormatting ? newValue : encodeValueObject(newValue);
		update((d) => ({ ...d, [key]: formattedNewValue }));
		setUserPreference(key, formattedNewValue);
	};

	try {
		const formattedValue = options?.skipValueFormatting ? value : decodeValueObject(value);

		return { value: formattedValue, setValue, error, loading };
	} catch (formattingError) {
		return {
			value: undefined,
			setValue,
			error: formattingError instanceof Error ? formattingError : error,
			loading,
		};
	}
};

export const useUserPreferencesInitialiser = (
	resource: RouteResource<UserPreferences>,
): {
	initialise: () => void;
} => {
	const { refresh, loading, error, data } = useResource<UserPreferences>(resource);

	const initialise = (): void => {
		if (data === null && error === null && !loading) {
			refresh();
		}
	};

	return { initialise };
};

const decodeValueObject = (value: string | null | undefined) => {
	if (!value) return value;

	return JSON.parse(value)?.value;
};

const encodeValueObject = (value: string) => {
	return JSON.stringify({ value });
};
